import { GraphResolverBase } from './GraphResolverBase';
import { ComponentFactory, ViewContainerRef } from '@angular/core';
import { ConfigDataService } from '../services/config-data.service';
import { GraphDataService } from '../services/graph-data-service.service';
import { ViewPeriodService } from '../services/view-period.service';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { PopoverController } from '@ionic/angular';
import { GenericBarChartData } from '../objects/chart';
import { CHART_PRIMARY_COLOR, CHART_SECONDARY_COLOR } from '../configs/color';
import { DynamicBarChartWrapperComponent } from '../shared-components/dynamic/dynamic-bar-chart-wrapper/dynamic-bar-chart-wrapper.component';
import { CustomerAgeTooltipsComponent } from '../pages/home/general/customer-age-tooltips/customer-age-tooltips.component';
import { showAgeCustomTooltips } from '../helpers/ageTooltips';
import { DynamicGraphAdditionalInput } from '../objects/config';
import { isScreenSmallerThanMD } from '../helpers/util';
import { getAdditionalInput } from '../helpers/DynamicGraphHelper';
import { generateNestedData } from '../helpers/mock-data-generator';

export class AgeProfileByPinResolver extends GraphResolverBase {
    public async createComponent(
        componentFactory: ComponentFactory<unknown>,
        additionalInput: string | DynamicGraphAdditionalInput | undefined,
        configDataService: ConfigDataService,
        graphDataService: GraphDataService,
        popoverController: PopoverController,
        viewPeriodService: ViewPeriodService,
        viewContainerRef: ViewContainerRef,
        subscription: Subscription
    ) {
        const includeAreaList = (getAdditionalInput(additionalInput, 'includeAreaList') || []) as string[];
        // load data here
        graphDataService.baseGraphData.addDependency(this.dataDependency);
        const isMockData = (getAdditionalInput(additionalInput, 'isMockData')) as boolean;
        // await configDataService.loadAppConfig();
        const chartLabel = configDataService.AGE_CLASS.map(age => configDataService.DISPLAY_LANGUAGE.AGE_CLASS[age]);
        const dualBarData$ = new BehaviorSubject<GenericBarChartData[]>([]);
        const componentRef = viewContainerRef.createComponent(componentFactory);
        const comInstance = componentRef.instance as DynamicBarChartWrapperComponent;

        if (isMockData) {
            const mockData = await generateNestedData(viewPeriodService.selectedDate, viewPeriodService, configDataService, 'AGE_PROFILE_BY_PIN', 'count', 5);
            let selectedInteractableName = graphDataService.baseGraphData.selectedInteractable$.getValue()?.name;
            if (includeAreaList.length > 0 && selectedInteractableName) {
                comInstance.isShow = includeAreaList.includes(selectedInteractableName);
            }
            dualBarData$.next([{
                data: mockData[selectedInteractableName].male,
                color: CHART_SECONDARY_COLOR,
                label: configDataService.DISPLAY_LANGUAGE.GENDER_CLASS.male,
                calPercentFrommAllData: true
            }, {
                data: mockData[selectedInteractableName].female,
                color: CHART_PRIMARY_COLOR,
                label: configDataService.DISPLAY_LANGUAGE.GENDER_CLASS.female,
                calPercentFrommAllData: true
            }]);
            subscription.add(combineLatest([graphDataService.baseGraphData.selectedInteractable$, viewPeriodService.dayList]).subscribe(([selectedInteractable, dayList]) => {
                // const ageData = graphDataService.baseGraphData.selectedInteractable$?.value?.type === 'gate' ? zoneInstanceName ? flattenData : ageDataByPin : ageBuildingData;
                if (selectedInteractable?.type !== 'gate') {
                    return;
                }
                selectedInteractableName = selectedInteractable?.name;
                if (includeAreaList.length > 0 && selectedInteractableName) {
                    comInstance.isShow = includeAreaList.includes(selectedInteractableName);
                }
                dualBarData$.next([{
                    data: mockData[selectedInteractableName].male,
                    color: CHART_SECONDARY_COLOR,
                    label: configDataService.DISPLAY_LANGUAGE.GENDER_CLASS.male,
                    calPercentFrommAllData: true
                }, {
                    data: mockData[selectedInteractableName].female,
                    color: CHART_PRIMARY_COLOR,
                    label: configDataService.DISPLAY_LANGUAGE.GENDER_CLASS.female,
                    calPercentFrommAllData: true
                }]);
            }));
        } else {
            dualBarData$.next([{
                data: graphDataService.ageProfileData$.value.female,
                color: CHART_SECONDARY_COLOR,
                label: configDataService.DISPLAY_LANGUAGE.GENDER_CLASS.female,
                calPercentFrommAllData: true
            }, {
                data: graphDataService.ageProfileData$.value.male,
                color: CHART_PRIMARY_COLOR,
                label: configDataService.DISPLAY_LANGUAGE.GENDER_CLASS.male,
                calPercentFrommAllData: true
            }]);
            subscription.add(combineLatest([graphDataService.buildingAgeProfileByPinBreakdown$]).subscribe(([ageDataByPin]) => {                // const ageData = graphDataService.baseGraphData.selectedInteractable$?.value?.type === 'gate' ? zoneInstanceName ? flattenData : ageDataByPin : ageBuildingData;
                if (!ageDataByPin) { return; }
                const selectedInteractableName = graphDataService.baseGraphData.selectedInteractable$.getValue()?.name;
                if (includeAreaList.length > 0 && selectedInteractableName) {
                    comInstance.isShow = includeAreaList.includes(selectedInteractableName);
                }
                dualBarData$.next([{
                    data: ageDataByPin.female,
                    color: CHART_SECONDARY_COLOR,
                    label: configDataService.DISPLAY_LANGUAGE.GENDER_CLASS.female,
                    calPercentFrommAllData: true
                }, {
                    data: ageDataByPin.male,
                    color: CHART_PRIMARY_COLOR,
                    label: configDataService.DISPLAY_LANGUAGE.GENDER_CLASS.male,
                    calPercentFrommAllData: true
                }]);
            }));
        }

        comInstance.title = configDataService.DISPLAY_LANGUAGE.AGE_PROFILE;
        comInstance.isLock = this.isLock;
        comInstance.infoPopover = async (e: any) => {
            const ageDetailPopover = await popoverController.create({
                component: CustomerAgeTooltipsComponent,
                cssClass: 'calendar-criteria-tooltips',
                event: e
            });
            return ageDetailPopover.present();
        };
        comInstance.isShowLegend = false;
        comInstance.data$ = dualBarData$;
        comInstance.chartLabel = chartLabel;
        comInstance.displayGrid = false;
        comInstance.displayAxis = 'X';
        comInstance.aspectRatio = isScreenSmallerThanMD(window) ? 1.1 : 1.4;
        comInstance.customToolTipFuction = function(tooltipModel) {
            showAgeCustomTooltips(tooltipModel, this, configDataService.ageImageSrc);
        };
        return comInstance;
    }

}
